/**
 * Transcript helpers shared by the session list, the session hub and the
 * session editor. Transcripts come in several shapes:
 *  - plain text (string or { content }),
 *  - Fathom / Recall utterance arrays (top-level array, or { transcript: [...] }),
 *  - Fathom `{ sentences: [...] }` from the import API.
 */

/**
 * Display name of an utterance's speaker.
 * @param {object} utterance - Utterance.
 * @returns {string} Speaker name.
 */
export function getSpeakerName(utterance) {
  return utterance?.speaker?.display_name || utterance?.speaker || utterance?.speaker_name || 'Speaker';
}

/**
 * Return a copy of the utterance with a new speaker name, keeping its shape.
 * @param {object} utterance - Utterance.
 * @param {string} newName - New display name.
 * @returns {object} Updated utterance.
 */
export function updateSpeakerName(utterance, newName) {
  if (utterance && typeof utterance.speaker === 'object' && utterance.speaker !== null) {
    return { ...utterance, speaker: { ...utterance.speaker, display_name: newName } };
  }
  return { ...utterance, speaker: newName, speaker_name: newName };
}

/**
 * Plain-text rendering of utterances: `[ts] Name: text` per line.
 * @param {object[]} utterances - Utterances.
 * @returns {string} Text.
 */
export function utterancesToText(utterances) {
  return (utterances || [])
    .map((u) => {
      const ts = u.timestamp ? `[${u.timestamp}] ` : '';
      return `${ts}${getSpeakerName(u)}: ${u.text || u.content || ''}`;
    })
    .join('\n');
}

/**
 * Normalise a raw transcript value (DB column, session object or API reply)
 * into an object with `content` (plain text) and, for structured sources,
 * the `transcript` utterance array.
 * @param {string|object|Array|null} raw - Raw transcript.
 * @returns {object|null} Normalised transcript, or null when empty.
 */
export function normaliseTranscript(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  if (Array.isArray(raw)) {
    return { transcript: raw, content: utterancesToText(raw), contentType: 'recall', source: 'recall' };
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return normaliseTranscript(parsed);
    } catch {
      // not JSON: plain text below
    }
    return { content: raw };
  }
  if (typeof raw === 'object') {
    if (!raw.content && Array.isArray(raw.transcript)) {
      return { ...raw, content: utterancesToText(raw.transcript) };
    }
    return raw;
  }
  return null;
}

/**
 * Whether a normalised transcript carries a structured utterance array.
 * @param {object|null} transcript - Normalised transcript.
 * @returns {boolean} True for structured transcripts.
 */
export function isStructuredTranscript(transcript) {
  return Boolean(transcript && Array.isArray(transcript.transcript) && transcript.transcript.length);
}

const BLOCK_SELECTOR = 'p,div,li,tr,h1,h2,h3,h4,h5,h6,blockquote,pre,section,article,header,footer';

/**
 * Regex fallback for {@link htmlToPlainText} when DOMParser is unavailable.
 * @param {string} source - HTML.
 * @returns {string} Plain text.
 */
function stripTagsWithRegex(source) {
  return source
    .replace(/<(script|style|template|noscript)\b[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6]|blockquote|pre)>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/**
 * Plain text of an HTML string, keeping paragraph and line breaks.
 *
 * The markup is parsed in an inert document (DOMParser), never through `innerHTML` on an element
 * of the live document, so untrusted markup such as `<img onerror>` cannot load resources or run
 * handlers. Without DOMParser (non-browser environments) tags are stripped with a regex.
 * @param {string} html - HTML (untrusted).
 * @returns {string} Plain text.
 */
export function htmlToPlainText(html) {
  const source = String(html || '');
  if (!source) return '';
  let text;
  if (typeof window === 'undefined' || typeof window.DOMParser !== 'function') {
    text = stripTagsWithRegex(source);
  } else {
    const doc = new window.DOMParser().parseFromString(source, 'text/html');
    const { body } = doc;
    if (!body) return '';
    body.querySelectorAll('script,style,template,noscript').forEach((node) => node.remove());
    body.querySelectorAll('br').forEach((node) => node.replaceWith(doc.createTextNode('\n')));
    body.querySelectorAll(BLOCK_SELECTOR).forEach((node) => node.append(doc.createTextNode('\n')));
    text = body.textContent || '';
  }
  return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Text content of an HTML string (for transcripts saved as HTML earlier). Text that does not look
 * like HTML is returned unchanged. Parsing is inert; see {@link htmlToPlainText}.
 * @param {string} content - Possibly-HTML text (untrusted).
 * @returns {string} Plain text.
 */
export function stripHtmlToText(content) {
  const text = String(content || '');
  if (!/<(p|div|br)\b/i.test(text)) return text;
  return htmlToPlainText(text);
}

/**
 * Extraction-type labels from a session's `extractionType` (string, JSON
 * array string or array).
 * @param {unknown} raw - Session extractionType.
 * @returns {string[]} Labels (may be empty).
 */
export function parseExtractionTypeLabels(raw) {
  let v = raw;
  if (typeof v === 'string' && v.trim().startsWith('[')) {
    try {
      v = JSON.parse(v);
    } catch {
      // keep as single label below
    }
  }
  if (Array.isArray(v)) return v.map((t) => String(t).trim()).filter(Boolean);
  if (v === null || v === undefined || v === '') return [];
  const single = String(v).trim();
  return single ? [single] : [];
}

/**
 * Recording duration in minutes for a Fathom meeting.
 * @param {object} meeting - Fathom meeting.
 * @returns {number|null} Minutes, or null when unknown.
 */
export function getMeetingDuration(meeting) {
  if (meeting.recording_start_time && meeting.recording_end_time) {
    return Math.round((new Date(meeting.recording_end_time) - new Date(meeting.recording_start_time)) / 60000);
  }
  if (meeting.scheduled_start_time && meeting.scheduled_end_time) {
    return Math.round((new Date(meeting.scheduled_end_time) - new Date(meeting.scheduled_start_time)) / 60000);
  }
  return null;
}

/**
 * Unique external attendee domains of a Fathom meeting.
 * @param {object} meeting - Fathom meeting.
 * @returns {string[]} Domains.
 */
export function getExternalDomains(meeting) {
  return [...new Set(
    (meeting.calendar_invitees || [])
      .filter((inv) => inv.is_external)
      .map((inv) => inv.email_domain)
      .filter(Boolean)
  )];
}

/**
 * Build the stored transcript object from a Fathom `get-transcript` reply.
 * Structured utterances are kept without a duplicated `content` string; the
 * extraction backend derives text from the array.
 * @param {unknown} rawTranscript - `transcriptResponse.transcript`.
 * @returns {object} Transcript object for the session row.
 */
export function buildFathomTranscriptObject(rawTranscript) {
  let structuredUtterances = null;
  let transcriptText = '';

  if (typeof rawTranscript === 'string') {
    transcriptText = rawTranscript;
  } else if (Array.isArray(rawTranscript)) {
    structuredUtterances = rawTranscript;
  } else if (rawTranscript && typeof rawTranscript === 'object') {
    if (typeof rawTranscript.transcript === 'string') {
      transcriptText = rawTranscript.transcript;
    } else if (Array.isArray(rawTranscript.transcript)) {
      structuredUtterances = rawTranscript.transcript;
    } else if (Array.isArray(rawTranscript.sentences)) {
      structuredUtterances = rawTranscript.sentences;
    } else {
      transcriptText = JSON.stringify(rawTranscript, null, 2);
    }
  }

  const transcriptObject = {
    contentType: 'fathom',
    source: 'fathom',
    processedAt: new Date().toISOString(),
  };
  if (structuredUtterances) {
    transcriptObject.transcript = structuredUtterances;
  } else {
    transcriptObject.content = transcriptText;
  }
  return transcriptObject;
}

/**
 * Human label for a session source.
 * @param {string} source - Session source.
 * @returns {string} Label.
 */
export function getSourceLabel(source) {
  if (source === 'fathom') return 'Fathom';
  if (source === 'recall') return 'Notetaker';
  return 'Upload';
}
